import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Card from '@/components/ui/Card'
import { Languages } from 'lucide-react'
import { cn } from '@/utils/cn'
import { quranTranslationLanguageLabel } from '@/utils/quranTranslationLanguages'

interface Ayah {
  number: number
  arabicText: string
  translations: {
    [language: string]: string
  }
}

interface TranslationViewProps {
  ayahs: Ayah[]
  availableLanguages: string[]
  defaultLanguage: string
  onLanguageChange?: (lang: string) => void
}

export default function TranslationView({
  ayahs,
  availableLanguages,
  defaultLanguage,
  onLanguageChange,
}: TranslationViewProps) {
  const { t } = useTranslation()
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage)
  const [viewMode, setViewMode] = useState<'side-by-side' | 'toggle'>('side-by-side')

  // Sync selectedLanguage with defaultLanguage when it changes
  useEffect(() => {
    if (defaultLanguage && defaultLanguage !== selectedLanguage) {
      setSelectedLanguage(defaultLanguage)
    }
  }, [defaultLanguage, selectedLanguage])

  const handleLanguageChange = (lang: string) => {
    setSelectedLanguage(lang)
    if (onLanguageChange) {
      onLanguageChange(lang)
    }
  }

  return (
    <div className="space-y-6">
      {/* Language Selector and View Mode */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Languages className="w-5 h-5 text-gray-600" />
            <label className="text-sm font-medium text-gray-700">{t('quran.translation.language', 'اللغة')}:</label>
            <select
              value={selectedLanguage}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {availableLanguages.map((lang) => (
                <option key={lang} value={lang}>
                  {quranTranslationLanguageLabel(lang)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('side-by-side')}
              className={cn(
                'px-3 py-1 text-sm rounded',
                viewMode === 'side-by-side'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              )}
            >
              {t('quran.translation.sideBySide', 'جنباً إلى جنب')}
            </button>
            <button
              onClick={() => setViewMode('toggle')}
              className={cn(
                'px-3 py-1 text-sm rounded',
                viewMode === 'toggle'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              )}
            >
              {t('quran.translation.stacked', 'تبديل')}
            </button>
          </div>
        </div>
      </Card>

      {/* Ayahs Display */}
      <Card className="p-6">
        <div className="space-y-6">
          {ayahs.map((ayah) => (
            <div key={ayah.number} className="space-y-3">
              <div className="flex items-start gap-4">
                <span className="text-primary-600 font-bold text-lg flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  {ayah.number}
                </span>
                {viewMode === 'side-by-side' ? (
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <p className="text-2xl text-gray-900 leading-relaxed text-right" dir="rtl">
                      {ayah.arabicText}
                    </p>
                    <p className="text-lg text-gray-700 leading-relaxed" dir="ltr">
                      {ayah.translations[selectedLanguage] || 
                       ayah.translations[defaultLanguage] || 
                       (Object.keys(ayah.translations).length > 0 ? Object.values(ayah.translations)[0] : '') ||
                       'Translation not available'}
                    </p>
                  </div>
                ) : (
                  <div className="flex-1">
                    <p className="text-2xl text-gray-900 leading-relaxed text-right mb-2" dir="rtl">
                      {ayah.arabicText}
                    </p>
                    <p className="text-lg text-gray-700 leading-relaxed" dir="ltr">
                      {ayah.translations[selectedLanguage] || 
                       ayah.translations[defaultLanguage] || 
                       (Object.keys(ayah.translations).length > 0 ? Object.values(ayah.translations)[0] : '') ||
                       'Translation not available'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}


